import React, { useState, useMemo, useEffect } from 'react';
import {
  Button,
  Autocomplete,
  TextField,
  InputAdornment,
  IconButton,
  FormControl,
  InputLabel,
  Tooltip,
  Select,
  MenuItem,
  SelectChangeEvent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';

import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';

import "../styles/assign-activity.css";
import AddEmployeeModal from '../components/assign/AddEmployeeModal';
import AssignActivityModal from '../components/assign/AssignActivityModal';
import { apiGet, apiPost, apiPostFormData, apiPut, API_BASE_URL } from "../api/http";
import { getAccessToken } from '../authService';
import { useAuth } from '../AuthContext';
import { dateFormat } from '../utils/dateFormat';

// Banner type for toast messages
type BannerType = { type: "success" | "error"; message: string } | null;

interface Employee {
  empCode: number;
  empName: string;
  emailId: string;
  techGroup?: string | null;
  displayName: string;
}

interface Category {
  id: number;
  name: string;
  status?: string;
}

interface Technology {
  id: number;
  name: string;
  status?: string;
}

interface BenchImportResponse {
  message: string;
  addedCount: number;
  skippedCount: number;
  invalidCount: number;
}

type LocalIconProps = {
    src: string;
    alt?: string;
    className?: string;
};

const LocalIcon: React.FC<LocalIconProps> = ({ src, alt = "", className }) => (
    <img
        src={src}
        alt={alt}
        className={className}
        loading="lazy"
    />
);

const ITEMS_PER_PAGE = 10;

// Rows are sorted by benchId descending — highest benchId (most recently added) appears first.

const AssignActivity: React.FC<{ sidebarOpen?: boolean }> = ({ sidebarOpen = false }) => {
  const { isAdmin } = useAuth();
  const [banner, setBanner] = useState<BannerType>(null);
  const [page, setPage] = useState(0);

  const [openAssignModal, setOpenAssignModal] = useState(false);
  const [openAddEmployee, setOpenAddEmployee] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const importFileInputRef = React.useRef<HTMLInputElement | null>(null);

  const [sortColumn, setSortColumn] = useState<string>("week");
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [filterTechGroup, setFilterTechGroup] = useState<string | null>(null);
  const [filterCategory, setFilterCategory] = useState<number | null>(null);
  const [filterTechnology, setFilterTechnology] = useState<number | null>(null);
  const [techGroupInput, setTechGroupInput] = useState("");
  const [categoryInput, setCategoryInput] = useState("");
  const [technologyInput, setTechnologyInput] = useState("");
  const [filterStatus, setFilterStatus] = useState<string | null>(null);
  const [search, setSearch] = useState('');
 

  const [categories, setCategories] = useState<Category[]>([]);
  const [technologies, setTechnologies] = useState<Technology[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [activitiesList, setActivitiesList] = useState<any[]>([]);
  const [activities, setActivities] = useState<any[]>([]);
  const [activityVersion, setActivityVersion] = useState(0);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | null>(null);
  const [weeks, setWeeks] = useState<any[]>([]);
  const [selectedWeek, setSelectedWeek] = useState<number | null>(null);
  const [presentWeekId, setPresentWeekId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [reassignConfirm, setReassignConfirm] = useState({
    open: false,
    week: '',
    activityName: '',
    employeeName: '',
    payload: null as any,
    benchId: null as number | null,
  });

  // ── Lifecycle ───────────────────────────────────────────────────────────────
  useEffect(() => {
    loadWeeks();
  }, []);

const loadWeeks = async () => {
  setLoading(true);
  try {
    const data: any[] = await apiGet("/bench/weeks");
    if (!data || data.length === 0) {
      setWeeks([]);
      setSelectedWeek(null);
      setActivities([]);
      return;
    }

    setWeeks(data);

    const currentWeek = data[0].id;
    setPresentWeekId(currentWeek);
    setSelectedWeek(currentWeek);

    // ✅ FIX: On page mount/return, check if there's a pinned benchId in sessionStorage.
    // This handles the case where the user assigned a row, navigated to MyActivity,
    // then came back — without the pin, fetchBenchData would sort purely by benchId desc
    // and the assigned row would go back to its natural position.
    let pinOnLoad: number | undefined;
    try {
      const s = sessionStorage.getItem('pinnedBenchId');
      if (s) pinOnLoad = Number(s);
    } catch {}

    await fetchBenchData(currentWeek, pinOnLoad);

  } catch (err) {
    console.error("Week load error", err);
    setWeeks([]);
  } finally {
    setLoading(false);
  }
};

  const handleWeekChange = (event: SelectChangeEvent<number>) => {
  const weekId = Number(event.target.value);
  setSelectedWeek(weekId);
  // ✅ Clear pin when user manually changes week — they're navigating intentionally
  try { sessionStorage.removeItem('pinnedBenchId'); } catch {}
  fetchBenchData(weekId);
};

  /**
   * Load bench rows from the server and apply the priority ordering so that
   * recently-updated / newly-added rows always appear at the top.
   */
 const fetchBenchData = async (weekId: number, pinBenchId?: number): Promise<any[]> => {
    try { if (!pinBenchId) { const s = sessionStorage.getItem('pinnedBenchId'); if (s) pinBenchId = Number(s); } } catch {}
    if (!weekId) return [];
    try {
      setLoading(true);
      const data = await apiGet<any[]>(`/activity/bench-with-activity?weekId=${weekId}`);
      let rows = (data ?? []).slice().sort((a, b) => Number(b.benchId) - Number(a.benchId));

      if (pinBenchId) {
        const idx = rows.findIndex(r => Number(r.benchId) === pinBenchId);
        if (idx > -1) {
          const [pinned] = rows.splice(idx, 1);
          rows.unshift(pinned);
        }
      }

      // ✅ FIX: Reset sort column and page SYNCHRONOUSLY in the same state batch
      // as setActivities — so React renders them together. Previously setSortColumn(null)
      // was called separately (in the event handler or onAssign) before the async fetch
      // completed, meaning the re-render after setActivities could still see the old
      // sortColumn value and re-sort the pinned row away from position 0.
      // Preserve previous row order for non-pinned rows
const prev = activities; // closure over current state
if (pinBenchId && prev && prev.length > 0) {
    const rest = rows.slice(1); // everything after pinned
    rest.sort((a: any, b: any) => {
        const prevIdxA = prev.findIndex((p: any) => Number(p.benchId) === Number(a.benchId));
        const prevIdxB = prev.findIndex((p: any) => Number(p.benchId) === Number(b.benchId));
        if (prevIdxA === -1 && prevIdxB === -1) return Number(b.benchId) - Number(a.benchId);
        if (prevIdxA === -1) return -1;
        if (prevIdxB === -1) return 1;
        return prevIdxA - prevIdxB;
    });
    rows.splice(1, rest.length, ...rest);
  }
  setSortColumn('week');
setPage(0);
setActivities(rows);
return rows;
    } catch (error) {
      console.error(error);
      setActivities([]);
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const [categoryData, techData, empData, activityData] = await Promise.all([
          apiGet<any[]>("/activity-categories"),
          apiGet<any[]>("/technology-details"),
          apiGet<any[]>("/winengage-emp/emp-details"),
          // Pull a large page so active options are not dropped from modal autocomplete.
          apiGet<any>("/Activity?PageNumber=1&PageSize=10000")
        ]);

        setCategories(
          (categoryData || []).map((c: any) => ({
            id: c.categoryId,
            name: c.categoryName,
            status: c.status ?? 'Active'
          }))
        );

        setTechnologies(
  (techData || []).map((t: any) => ({
    id: t.techGroupId,
    name: t.technologyName,
    status: t.status
  }))
);

        setEmployees(
          (empData || []).map((e: any) => ({
            empCode: e.empCode,
            empName: e.empName?.trim() || "",
            emailId: e.email,
            displayName: e.displayName
          }))
        );

        setActivitiesList(activityData?.items || []);
      } catch (error) {
        console.error("Error loading dropdown data:", error);
      }
    };
    fetchDropdownData();
  }, [activityVersion]);

  
const selectedWeekRef = React.useRef<number | null>(null);

    // Keep ref in sync — add this right after the selectedWeek state declaration
    useEffect(() => {
      selectedWeekRef.current = selectedWeek;
    }, [selectedWeek]);

    // Listen for external assignments (e.g., from MyActivity) so we can pin the
    // newly-assigned bench to the top of the table when it happens elsewhere.
    // Listen for external assignments (e.g., from MyActivity)
    useEffect(() => {
     const handler = async () => {
      try {
        const s = sessionStorage.getItem('pinnedBenchId');
        const pin = s ? Number(s) : null;
        const currentWeek = selectedWeekRef.current;
        if (!currentWeek) return;

        if (pin) {
          const rows = await fetchBenchData(currentWeek, pin);
          if (!(rows || []).some(r => Number(r.benchId) === Number(pin))) {
            for (const w of weeks) {
              const wRows = await fetchBenchData(w.id, pin);
              if ((wRows || []).some(r => Number(r.benchId) === Number(pin))) {
                setSelectedWeek(w.id);
                return;
              }
            }
          }
        } else {
          await fetchBenchData(currentWeek);
        }
      } catch (e) {}
    };
      window.addEventListener('benchUpdated', handler as any);
      return () => window.removeEventListener('benchUpdated', handler as any);
    }, [weeks]); // using ref for selectedWeek to avoid stale closures



  // ── Sorting ─────────────────────────────────────────────────────────────────
  const handleSort = (column: string) => {
    if (column === 'action') return;
    // Disable sorting if there are no records
    if (sortedData.length === 0) return;
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const renderSortIcon = (column: string) => {
    if (column === 'action' || column === 'week') return null;
    // Disable sort icons if there are no records
    if (sortedData.length === 0) {
      return <span className="sort-icons disabled"><ArrowUpwardIcon className="sort-icon" /><ArrowDownwardIcon className="sort-icon" /></span>;
    }
    const isActive = sortColumn === column;
    return (
      <span className="sort-icons">
        <ArrowUpwardIcon className={`sort-icon ${isActive && sortDirection === 'asc' ? 'active' : ''}`} />
        <ArrowDownwardIcon className={`sort-icon ${isActive && sortDirection === 'desc' ? 'active' : ''}`} />
      </span>
    );
  };

  // ── Derived data ─────────────────────────────────────────────────────────────
  const baseRows = useMemo(() => {
    return (activities || []).map((item: any) => ({
      id: item.benchId,
      weekStartDate: item.weekStartDate || '-',
      weekId: item.weekID ?? item.weekId ?? item.week ?? null,
      empId: item.empCode,
      name: item.empName ?? '',
      email: item.email || item.Email || item.emailID || item.emailId || '-',
      techGroup: item.techGroup ?? '',
      category: item.categoryName ?? '',
      technology: item.technologyName ?? '',
      categoryId: item.categoryId ?? null,
      technologyId: item.technologyId ?? null,
      activityId: item.activityId ?? item.activityID ?? item.ActivityId ?? null,
      activityName: item.activityName ?? '',
      description: item.activityDescription ?? '',
      status: item.activityStatus ?? '',
      startDate: item.startDate ?? '-',
      endDate: item.endDate ?? '-',
      hours: item.totalHours ?? item.hours ?? '-',
      techGroupName: item.techGroupName,
    }));
  }, [activities]);

    const mappedData = useMemo(() => {
      const searchText = search?.toLowerCase().trim();

      return (baseRows || []).filter((row: any) => {
        // Only search visible columns
        const searchFields = [
          row.weekStartDate ? dateFormat(row.weekStartDate) : '',
          row.empId,
          row.name,
          row.email,
          row.techGroupName,
          row.category,
          row.technology,
          row.activityName,
          row.status,
          row.startDate ? dateFormat(row.startDate) : '',
          row.endDate ? dateFormat(row.endDate) : '',
          row.hours
        ];
        const matchesSearch =
          !searchText ||
          searchFields.some(val => val && val.toString().toLowerCase().includes(searchText));

        return (
          (!filterTechGroup ||
            row.techGroupName?.toLowerCase().includes(filterTechGroup.toLowerCase())) &&
          (!filterCategory || row.categoryId === filterCategory) &&
          (!filterTechnology || row.technologyId === filterTechnology) &&
          (!filterStatus || row.status === filterStatus) &&
          matchesSearch
        );
      });
    }, [baseRows, filterTechGroup, filterCategory, filterTechnology, filterStatus, search]);

  // Filter option derivations (unchanged logic, just cleaner)
  const techGroupOptions = useMemo(() => {
    const map = new Map<string, { techGroupName: string }>();
    const s = search?.toLowerCase().trim();
    baseRows.forEach(r => {
      if ((!filterCategory || r.categoryId === filterCategory) &&
          (!filterTechnology || r.technologyId === filterTechnology) &&
          (!filterStatus || r.status === filterStatus) &&
          (!s || r.name.toLowerCase().includes(s) || r.email.toLowerCase().includes(s) || r.empId?.toString().includes(s) || r.activityName.toLowerCase().includes(s))) {
        if (r.techGroupName) map.set(r.techGroupName, { techGroupName: r.techGroupName });
      }
    });
    return Array.from(map.values()).sort((a, b) => (a.techGroupName || '').toLowerCase().localeCompare((b.techGroupName || '').toLowerCase()));
  }, [baseRows, filterCategory, filterTechnology, filterStatus, search]);

  const categoryOptions = useMemo(() => {
    const map = new Map<number, { id: number; name: string }>();
    const s = search?.toLowerCase().trim();
    baseRows.forEach(r => {
      if ((!filterTechGroup || r.techGroupName?.toLowerCase().includes(filterTechGroup.toLowerCase())) &&
          (!filterTechnology || r.technologyId === filterTechnology) &&
          (!filterStatus || r.status === filterStatus) &&
          (!s || r.name.toLowerCase().includes(s) || r.email.toLowerCase().includes(s) || r.empId?.toString().includes(s) || r.activityName.toLowerCase().includes(s))) {
        if (r.categoryId != null) map.set(r.categoryId, { id: r.categoryId, name: r.category });
      }
    });
    return Array.from(map.values()).sort((a, b) => (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase()));
  }, [baseRows, filterTechGroup, filterTechnology, filterStatus, search]);

  const technologyOptions = useMemo(() => {
    const map = new Map<number, { id: number; name: string }>();
    const s = search?.toLowerCase().trim();
    baseRows.forEach(r => {
      if ((!filterTechGroup || r.techGroupName?.toLowerCase().includes(filterTechGroup.toLowerCase())) &&
          (!filterCategory || r.categoryId === filterCategory) &&
          (!filterStatus || r.status === filterStatus) &&
          (!s || r.name.toLowerCase().includes(s) || r.email.toLowerCase().includes(s) || r.empId?.toString().includes(s) || r.activityName.toLowerCase().includes(s))) {
        if (r.technologyId != null) map.set(r.technologyId, { id: r.technologyId, name: r.technology });
      }
    });
    return Array.from(map.values()).sort((a, b) => (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase()));
  }, [baseRows, filterTechGroup, filterCategory, filterStatus, search]);

  const statusOptions = useMemo(() => {
    const set = new Set<string>();
    const s = search?.toLowerCase().trim();
    baseRows.forEach(r => {
      if ((!filterTechGroup || r.techGroupName?.toLowerCase().includes(filterTechGroup.toLowerCase())) &&
          (!filterCategory || r.categoryId === filterCategory) &&
          (!filterTechnology || r.technologyId === filterTechnology) &&
          (!s || r.name.toLowerCase().includes(s) || r.email.toLowerCase().includes(s) || r.empId?.toString().includes(s) || r.activityName.toLowerCase().includes(s))) {
        if (r.status) set.add(String(r.status));
      }
    });
    return Array.from(set.values()).sort((a, b) => (a || '').toLowerCase().localeCompare((b || '').toLowerCase()));
  }, [baseRows, filterTechGroup, filterCategory, filterTechnology, search]);

  const weekOptions = useMemo(() => {
    const map = new Map<number, { id: number; startDate: string }>();
    baseRows.forEach(r => {
      const id = Number(r.weekId || 0);
      if (id) map.set(id, { id, startDate: r.weekStartDate });
    });
    return Array.from(map.values()).sort((a, b) => {
      const ta = Number(new Date(a.startDate || '').getTime()) || 0;
      const tb = Number(new Date(b.startDate || '').getTime()) || 0;
      if (ta !== tb) return ta - tb;
      return String(a.startDate || '').localeCompare(String(b.startDate || ''));
    });
  }, [baseRows]);

    // Reset page on filter/search change
    useEffect(() => { setPage(0); }, [filterTechGroup, filterCategory, filterTechnology, filterStatus, search]);

  /**
   * Sort: when user has clicked a column, apply that sort.
   * Otherwise preserve the priority order that was set when rows were loaded.
   */
  const sortedData = useMemo(() => {
    if (!sortColumn) return mappedData; // priority order already baked in

    return [...mappedData].sort((a, b) => {
      let valA = (a as any)[sortColumn];
      let valB = (b as any)[sortColumn];
      // Special handling for Tech Group column
      if (sortColumn === 'techGroup' || sortColumn === 'techGroupName') {
        valA = a.techGroupName || a.techGroup || '';
        valB = b.techGroupName || b.techGroup || '';
      }
      // Special handling for hours (Total Hours) column
      if (sortColumn === 'hours') {
        const numA = isNaN(Number(valA)) ? 0 : Number(valA);
        const numB = isNaN(Number(valB)) ? 0 : Number(valB);
        return sortDirection === 'asc' ? numA - numB : numB - numA;
      }
      if (valA == null) return 1;
      if (valB == null) return -1;
      if (sortColumn.toLowerCase().includes("date") || sortColumn === "weekStartDate") {
        return sortDirection === "asc"
          ? new Date(valA).getTime() - new Date(valB).getTime()
          : new Date(valB).getTime() - new Date(valA).getTime();
      }
      if (!isNaN(valA) && !isNaN(valB)) {
        return sortDirection === "asc" ? Number(valA) - Number(valB) : Number(valB) - Number(valA);
      }
      return sortDirection === "asc"
        ? String(valA).localeCompare(String(valB))
        : String(valB).localeCompare(String(valA));
    });
  }, [mappedData, sortColumn, sortDirection]);

  // ── Handlers ─────────────────────────────────────────────────────────────────
  const handleCreateEmployees = async (employeesParam: any[]) => {
    try {
      const empCodes = (employeesParam || [])
        .map((emp: any) => Number(emp.empCode))
        .filter((code: number) => Number.isFinite(code) && code > 0);

      await apiPost<any>("/bench/bulk", empCodes);

      // Clear filters and search
      setFilterTechGroup(null);
      setFilterCategory(null);
      setFilterTechnology(null);
      setFilterStatus(null);
      setSearch("");
      // Close modal, reset sort so benchId desc order is active
      setOpenAddEmployee(false);
      setBanner({ type: 'success', message: 'New employee added successfully' });
      setSortColumn('week');
      setPage(0);
      // Fetch fresh data — new rows have highest benchId so they appear first automatically
      if (selectedWeek) {
        try { sessionStorage.removeItem('pinnedBenchId'); } catch {}
        await fetchBenchData(selectedWeek);
      }
    } catch (err) {
      console.error("Error creating bench:", err);
      setBanner({ type: 'error', message: 'Failed to add employee. Please try again.' });
      throw err;
    }
    };

  const handleImportClick = () => {
    importFileInputRef.current?.click();
  };

  const handleExportClick = async () => {
    if (!selectedWeek || isExporting) {
      return;
    }

    const selectedWeekObj = weeks.find((w: any) => Number(w.id) === Number(selectedWeek));
    const toFileDate = (value: string | null | undefined) => {
      if (!value) return '';
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return '';
      return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-');
    };

    const startForName = toFileDate(selectedWeekObj?.startDate);
    const endForName = toFileDate(selectedWeekObj?.endDate);
    const fallbackFileName = `Bench Data - ${startForName} - ${endForName}.xlsx`;

    try {
      setIsExporting(true);
      const token = await getAccessToken();

      const response = await fetch(`${API_BASE_URL}/bench/export?weekId=${selectedWeek}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to export bench data.');
      }

      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition') || '';
      const fileNameMatch = /filename\*=UTF-8''([^;]+)|filename=\"?([^\";]+)\"?/i.exec(disposition);
      const fileName = decodeURIComponent(fileNameMatch?.[1] || fileNameMatch?.[2] || fallbackFileName);

      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setBanner({ type: 'error', message: 'Export failed. Please try again.' });
    } finally {
      setIsExporting(false);
    }
  };

  const handleImportFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setIsImporting(true);

      const formData = new FormData();
      formData.append('file', file);

      const result = await apiPostFormData<BenchImportResponse>("/bench/import", formData);

      const serverMessage = (result?.message || '').toLowerCase();
      const isAlreadyAdded =
        serverMessage.includes('already been added') ||
        serverMessage.includes('already been imported') ||
        serverMessage.includes('record already exists');

      const alreadyImportedMessage = 'The data has already been imported and cannot be added again.';

      setBanner({
        type: isAlreadyAdded ? 'error' : 'success',
        message: isAlreadyAdded ? alreadyImportedMessage : 'File imported successfully.',
      });

      if (!isAlreadyAdded && selectedWeek) {
        try { sessionStorage.removeItem('pinnedBenchId'); } catch {}
        await fetchBenchData(selectedWeek);
      }
    } catch (err) {
      const raw = err instanceof Error ? err.message : String(err);
      const lowered = raw.toLowerCase();

      if (lowered.includes('please add a correct file') || lowered.includes('invalid file')) {
        setBanner({ type: 'error', message: 'Invalid file selected. Please upload a valid file and try again.' });
      } else if (lowered.includes('already been added') || lowered.includes('already been imported') || lowered.includes('record already exists')) {
        setBanner({ type: 'error', message: 'The data has already been imported and cannot be added again.' });
      } else {
        setBanner({ type: 'error', message: 'Import failed. Please try again.' });
      }
    } finally {
      setIsImporting(false);
      event.target.value = '';
    }
  };

    const paginatedFilteredData = React.useMemo(() => {
        const start = page * ITEMS_PER_PAGE;
        return sortedData.slice(start, start + ITEMS_PER_PAGE);
    }, [sortedData, page]);

    const totalPages = Math.max(
        1,
        Math.ceil(sortedData.length / ITEMS_PER_PAGE)
    );

    const isPrevDisabled = page === 0;
    const isNextDisabled = page >= totalPages - 1;
    const isPastWeekSelected =
      selectedWeek !== null &&
      presentWeekId !== null &&
      selectedWeek < presentWeekId;

  const normalizeActivityName = (name: string) =>
    (name || '').toLowerCase().replace(/[^a-z0-9]/gi, '');

  const executeAssignment = async (payload: any, benchId: number) => {
    try { sessionStorage.setItem('pinnedBenchId', String(Number(benchId))); } catch {}

    // Keep existing optimistic update behavior.
    setActivities(prev => {
      const arr = Array.isArray(prev) ? [...prev] : [];
      const idx = arr.findIndex(a => Number(a.benchId) === Number(benchId));
      if (idx > -1) {
        const updated = {
          ...arr[idx],
          activityId: payload.activityId || null,
          activityName: payload.name || arr[idx].activityName,
          activityDescription: payload.description || arr[idx].activityDescription,
          categoryId: payload.categoryId ?? arr[idx].categoryId,
          categoryName: (payload.categoryId && categories.find(c => c.id === payload.categoryId)?.name) || arr[idx].categoryName,
          technologyId: payload.technologyId ?? arr[idx].technologyId,
          technologyName: (payload.technologyId && technologies.find(t => t.id === payload.technologyId)?.name) || arr[idx].technologyName,
          activityStatus: 'Active',
          startDate: Date.now(),
          endDate: Date.now(),
          hours: payload.hours ?? arr[idx].hours
        };
        arr.splice(idx, 1);
        return [updated, ...arr];
      }
      return arr;
    });

    setPage(0);
    setSortColumn('week');

    (async () => {
      try {
        let activityIdToAssign = payload.activityId;

        if (payload.isNew) {
          const created = await apiPut<any>(`/assign-activity/${benchId}/assign`, {
            activityId: activityIdToAssign,
            name: payload.name,
            description: payload.description,
            status: 'Yet to start',
            categoryId: payload.categoryId,
            technologyId: payload.technologyId,
            pointOfContactId: payload.pointOfContactId,
          });
          activityIdToAssign = created?.activityId ?? created?.id ?? created?.activityID;
        }

        if (activityIdToAssign) {
          await apiPut<any>(`/assign-activity/${benchId}/assign`, {
            activityId: activityIdToAssign,
            startDate: new Date(),
            endDate: new Date(),
            hours: payload.hours,
            status: 'Active',
          });
        }
         setActivityVersion(v => v + 1);

        if (selectedWeek) await fetchBenchData(selectedWeek, Number(benchId));

        try { window.dispatchEvent(new CustomEvent('benchUpdated')); } catch {}
      } catch (err) {
        console.error('Background assignment failed:', err);
        if (selectedWeek) await fetchBenchData(selectedWeek);
      }
    })();

    setBanner({ type: 'success', message: 'Activity assigned successfully' });
  };

  const findCompletedInPastWeek = async (benchId: number, payload: any) => {
    const benchRecord = (activities || []).find(a => Number(a.benchId) === Number(benchId));
    const empCode = benchRecord?.empCode ?? benchRecord?.empId ?? null;
    if (!empCode) return null;

    const currentWeekStart = new Date(benchRecord?.weekStartDate || '');
    const hasCurrentWeekDate = !Number.isNaN(currentWeekStart.getTime());
    const targetActivityId = payload?.activityId ?? null;
    const targetNameNorm = normalizeActivityName(payload?.name || benchRecord?.activityName || '');

    const priorWeeks = (weeks || [])
      .filter((w: any) => {
        if (!w?.startDate) return false;
        const d = new Date(w.startDate);
        if (Number.isNaN(d.getTime())) return false;
        if (hasCurrentWeekDate) return d.getTime() < currentWeekStart.getTime();
        return selectedWeek != null ? Number(w.id) !== Number(selectedWeek) : true;
      })
      .sort((a: any, b: any) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());

    for (const w of priorWeeks) {
      try {
        const weekRows: any[] = await apiGet<any[]>(`/activity/bench-with-activity?weekId=${w.id}`);
        const row = (weekRows || []).find((r: any) => {
          const rowEmp = r.empCode ?? r.empId;
          if (Number(rowEmp) !== Number(empCode)) return false;

          const rowStatus = String(r.activityStatus || r.status || '').toLowerCase();
          if (rowStatus !== 'completed') return false;

          const rowActivityId = r.activityId ?? r.activityID ?? r.ActivityId ?? null;
          if (targetActivityId && rowActivityId) {
            return Number(rowActivityId) === Number(targetActivityId);
          }

          const rowNameNorm = normalizeActivityName(r.activityName || r.name || '');
          return Boolean(targetNameNorm) && rowNameNorm === targetNameNorm;
        });

        if (row) {
          return {
            week: row.weekStartDate || w.startDate,
            activityName: row.activityName || payload?.name || '',
            employeeName: row.empName || benchRecord?.empName || '',
          };
        }
      } catch {
        // Ignore week-level fetch errors and continue scanning other weeks.
      }
    }

    return null;
  };

  return (
   <div className="assign-activity-container assign-layout">
     <div className={`activities-top ${isPastWeekSelected ? 'with-helper-text' : ''}`}>
      <div className="assign-activity-heading-block">
        {/* HEADER */}
        <h2 className="categories-heading">Assign Activity</h2>
        {isPastWeekSelected && (
          <div className="assign-week-helper-text" role="status" aria-live="polite">
            <InfoOutlinedIcon className="assign-week-helper-icon" fontSize="small" />
            <span>Assignments allowed only for current week</span>
          </div>
        )}
      </div>

      <div className="assign-activity-controls">
        <Button
          variant="contained"
          onClick={() => setOpenAddEmployee(true)}
          size="small"
          className="btn-primary"
          color={isPastWeekSelected ? 'inherit' : 'primary'}
          disabled={isPastWeekSelected}
          sx={isPastWeekSelected ? {
            backgroundColor: '#e0e0e0 !important',
            color: '#888 !important',
            borderColor: '#e0e0e0 !important',
            cursor: 'not-allowed !important',
            '&:hover': {
              backgroundColor: '#e0e0e0 !important',
              color: '#888 !important',
            },
          } : {}}
        >
          <LocalIcon src="/icons/AddIcon.svg" className="btn-icon" />
          Add Employee
        </Button>

        {/* WEEK */}
        <Autocomplete
          size="small"
          className="filter-week"
          options={weeks}
          popupIcon={undefined}
          forcePopupIcon={true}
          getOptionLabel={(o) => (typeof o === 'string' ? o : o && o.startDate ? dateFormat(o.startDate) : '')}
          value={weeks.find(w => w.id === selectedWeek) || null}
          onChange={(_, v) => {
            setSelectedWeek(v ? v.id : null);
            if (v && v.id) {
              fetchBenchData(v.id);
            }
          }}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          disableClearable
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Week"
              variant="outlined"
              className="filter-input"
              inputProps={{
                ...params.inputProps,
                style: { minWidth: 90, maxWidth: 180, width: 'auto' }
              }}
            />
          )}
        />

              {/* TECH GROUP */}
                <Autocomplete
                  className="filter-autocomplete"
                  size="small"
                  options={techGroupOptions}
                  filterOptions={(options, { inputValue }) =>
  options.filter(option =>
    option.techGroupName.toLowerCase().includes(inputValue.toLowerCase())
  )
}
                  noOptionsText="No options"
                  clearIcon={null}
                  popupIcon={undefined}
                  forcePopupIcon={true}
                  value={typeof filterTechGroup === 'string' ? { techGroupName: filterTechGroup } : techGroupOptions.find(o => o.techGroupName === filterTechGroup) || null}
                  inputValue={techGroupInput}
                  onInputChange={(_, v) => setTechGroupInput(v)}
                  getOptionLabel={(o) => typeof o === 'string' ? o : o.techGroupName}
                  isOptionEqualToValue={(option, value) =>
  option.techGroupName === value.techGroupName
}
                  onChange={(_, v) => {
                    if (typeof v === 'string') {
                      setFilterTechGroup(v);
                      setTechGroupInput(v);
                    } else {
                      setFilterTechGroup(v ? v.techGroupName : null);
                      setTechGroupInput(v ? v.techGroupName : "");
                    }
                  }}
                  clearOnEscape
                  renderInput={(params) => {
  const { InputProps, ...rest } = params;

  return (
    <TextField
      {...rest}
      InputProps={{
        ...InputProps,
        endAdornment: (
          <>
            {(techGroupInput || filterTechGroup) && (
              <IconButton
                size="small"
                aria-label="clear tech group filter"
                onClick={() => {
                  setFilterTechGroup(null);
                  setTechGroupInput("");
                }}
                edge="end"
                sx={{
  padding: '2px',
  position: 'absolute',
  right: '26px',
  zIndex: 1,
  '@media (prefers-color-scheme: dark)': {
    color: '#fff',
    '& .MuiSvgIcon-root': {
      color: '#fff',
    },
  },
}}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            )}

            {InputProps.endAdornment}
          </>
        )
      }}
      placeholder="Tech Group"
      variant="outlined"
      className="filter-input"
    />
  );
}}
                />

              {/* CATEGORY */}
                <Autocomplete
                  className="filter-autocomplete"
                  size="small"
                  options={categoryOptions}
                  filterOptions={(options, { inputValue }) =>
  options.filter(option =>
    option.name.toLowerCase().includes(inputValue.toLowerCase())
  )
}
                  noOptionsText="No options"
                  clearIcon={null}
                  popupIcon={undefined}
                  forcePopupIcon={true}
                  value={categoryOptions.find(o => o.id === filterCategory) || null}
                  inputValue={categoryInput}
                  onInputChange={(_, v) => setCategoryInput(v)}
                  getOptionLabel={(o) => typeof o === 'string' ? o : o.name}
                  isOptionEqualToValue={(option, value) => !!option && !!value && option.id === value.id}
                  onChange={(_, v) => {
                    if (typeof v === 'string') {
                      setFilterCategory(null);
                      setCategoryInput(v);
                    } else {
                      setFilterCategory(v ? v.id : null);
                      setCategoryInput(v ? v.name : "");
                    }
                  }}
                  clearOnEscape
                  renderInput={(params) => {
  const { InputProps, ...rest } = params;

  return (
    <TextField
      {...rest}
      InputProps={{
        ...InputProps,
        endAdornment: (
          <>
            {(categoryInput || filterCategory) && (
              <IconButton
                size="small"
                aria-label="clear category filter"
                onClick={() => {
                  setFilterCategory(null);
                  setCategoryInput("");
                }}
                edge="end"
                sx={{
  padding: '2px',
  position: 'absolute',
  right: '26px',
  zIndex: 1,
  '@media (prefers-color-scheme: dark)': {
    color: '#fff',
    '& .MuiSvgIcon-root': {
      color: '#fff',
    },
  },
}}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            )}

            {InputProps.endAdornment}
          </>
        )
      }}
      placeholder="Category"
      variant="outlined"
      className="filter-input"
    />
  );
}}
                />

        {/* TECHNOLOGY */}
        <Autocomplete
          className="filter-autocomplete"
          size="small"
          options={technologyOptions}
          filterOptions={(options, { inputValue }) =>
            options.filter(option =>
              option.name.toLowerCase().includes(inputValue.toLowerCase())
            )
          }
          noOptionsText="No options"
          clearIcon={null}
          popupIcon={undefined}
          forcePopupIcon={true}
          value={technologyOptions.find(o => o.id === filterTechnology) || null}
          inputValue={technologyInput}
          onInputChange={(_, v) => setTechnologyInput(v)}
          getOptionLabel={(o) => typeof o === 'string' ? o : o.name}
          onChange={(_, v) => {
            if (typeof v === 'string') {
              setFilterTechnology(null);
              setTechnologyInput(v);
            } else {
              setFilterTechnology(v ? v.id : null);
              setTechnologyInput(v ? v.name : "");
            }
          }}
          clearOnEscape
          renderInput={(params) => {
            const { InputProps, ...rest } = params;

            return (
              <TextField
                {...rest}
                InputProps={{
                  ...InputProps,
                  endAdornment: (
                    <>
                      {(technologyInput || filterTechnology) && (
                        <IconButton
                          size="small"
                          aria-label="clear technology filter"
                          onClick={() => {
                            setFilterTechnology(null);
                            setTechnologyInput("");
                          }}
                          edge="end"
                          sx={{
                            padding: '2px',
                            position: 'absolute',
                            right: '26px',
                            zIndex: 1,
                            '@media (prefers-color-scheme: dark)': {
                              color: '#fff',
                              '& .MuiSvgIcon-root': {
                                color: '#fff',
                              },
                            },
                          }}
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      )}

                      {InputProps.endAdornment}
                    </>
                  )
                }}
                placeholder="Technology"
                variant="outlined"
                className="filter-input"
              />
            );
          }}
        />

        {/* SEARCH */}
        <TextField
          className="filter-search-input"
          size="small"
          placeholder="Search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon className="search-icon" />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label="clear search"
                  onClick={() => setSearch('')}
                  edge="end"
                  sx={{
                    '@media (prefers-color-scheme: dark)': {
                      color: '#fff',
                      '& .MuiSvgIcon-root': {
                        color: '#fff',
                      },
                    },
                  }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />

        <input
          ref={importFileInputRef}
          type="file"
          accept=".xlsx,.xls"
          style={{ display: 'none' }}
          onChange={handleImportFileChange}
        />

        {!isPastWeekSelected && (
          <>
            <Tooltip title="Export to Excel" arrow>
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  className="btn-export-excel-icon"
                  onClick={handleExportClick}
                  disabled={isExporting}
                  aria-label="Export to Excel"
                >
                  <FileDownloadOutlinedIcon fontSize="small" />
                </Button>
              </span>
            </Tooltip>

            {isAdmin && (
              <Tooltip title="Import from Excel" arrow>
                <span>
                  <Button
                    variant="outlined"
                    size="small"
                    className="btn-import-excel-icon"
                    onClick={handleImportClick}
                    disabled={isImporting}
                    aria-label="Import from Excel"
                  >
                    <UploadFileOutlinedIcon fontSize="small" />
                  </Button>
                </span>
              </Tooltip>
            )}
          </>
        )}

        {isPastWeekSelected && (
          <Tooltip title="Export from Excel" arrow>
            <span>
              <Button
                variant="outlined"
                size="small"
                className="btn-export-excel-icon"
                onClick={handleExportClick}
                disabled={isExporting}
                aria-label="Export from Excel"
              >
                <FileDownloadOutlinedIcon fontSize="small" />
              </Button>
            </span>
          </Tooltip>
        )}
      </div>

      </div>

      {/* TABLE */}
          <div className={`assign-table-card ${isPastWeekSelected ? 'with-week-helper' : ''}`}>
              {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                  <CircularProgress />
                </div>
              ) : (
                <table className="assign-table">
                <thead>
                  <tr>
                      {['week', 'empId', 'name', 'email', 'techGroup',
                                  'activityName', 'category', 'technology', 
                                  'status', 'startDate', 'endDate', 'hours'
                              ].filter(col => !sidebarOpen || !['email', 'startDate', 'endDate'].includes(col))
                      .map((col) => (
                      <th
                        key={col}
                        className={`col-${col}`}
                        style={{ cursor: "pointer", userSelect: "none" }}
                        onClick={() => handleSort(col)}
                      >
                        {col === 'empId' ? 'Emp Id' :
                          col === 'name' ? 'Emp Name' :
                            col === 'techGroup' ? 'Tech Group' :
                              col === 'activityName' ? 'Name' :
                                col === 'startDate' ? 'Start Date' :
                                  col === 'endDate' ? 'End Date' :
                                    col === 'hours' ? 'Total Hours' :
                                      col.charAt(0).toUpperCase() + col.slice(1)}
                        {renderSortIcon(col)}
                      </th>
                    ))}
                    <th className="col-action">Action</th>
                  </tr>
                </thead>

                <tbody>
                  {paginatedFilteredData.length === 0 ? (
                    <tr>
                       <td colSpan={10} align="center" className="empty-state">
                                 <div className="empty-content">
                                   <span className="th-inner">No Search Results found.<br/>
                                   Try a different search.
                                   </span>
                                 </div>
                       </td>
                    </tr>
                  ) : (
                    paginatedFilteredData.map((row, index) => (
                      <tr key={`${page}-${index}`}>
                            <td className="col-week">{dateFormat(row.weekStartDate)}</td>
                            <td className="col-empId" style={{ minWidth: 40, maxWidth: 40, width: 40, textAlign: 'center' }}>{row.empId}</td>
                            <td className="col-name">
                              <Tooltip title={row.name && row.name.length > 20 ? row.name : ''} placement="top" arrow disableInteractive>
                                <span className="truncate-text">{row.name}</span>
                              </Tooltip>
                            </td>
                            {!sidebarOpen && (
                              <td className="col-email">
                                <Tooltip title={row.email && row.email.length > 20 ? row.email : ''} placement="top" arrow disableInteractive>
                                  <span className="truncate-text">{row.email}</span>
                                </Tooltip>
                              </td>
                            )}
                            <td className="col-techGroup">{row.techGroupName}</td>
                            <td className="col-activityName">
                              <Tooltip title={row.activityName && row.activityName.length > 20 ? row.activityName : ''} placement="top" arrow disableInteractive>
                                <span className="truncate-text">{row.activityName}</span>
                              </Tooltip>
                            </td>
                            <td className="col-category">
                              <Tooltip title={row.category && row.category.length > 20 ? row.category : ''} placement="top" arrow disableInteractive>
                                <span className="truncate-text">{row.category}</span>
                              </Tooltip>
                            </td>
                            <td className="col-technology">{row.technology}</td>
                            <td className="col-status">{row.status}</td>
                            {!sidebarOpen && <td className="col-startDate">{dateFormat(row.startDate)}</td>}
                            {!sidebarOpen && <td className="col-endDate">{dateFormat(row.endDate)}</td>}
                            <td className="col-hours">{
                              row.hours != null && row.hours !== '-' && !isNaN(Number(row.hours)) && Number(row.hours) !== 0
                                ? Number(row.hours).toFixed(2)
                                : ''
                            }</td>
                        <td className="action-cell">
                          {(() => {
                            const isAssigned = Boolean(row.activityId) ||
                              Boolean((row.activityName || '').toString().trim()) ||
                              Boolean((row.status || '').toString().trim());
                            // Only allow assigning for the present week. Disable for any
                            // other week (previous or future) and for already-assigned rows.
                            const rowWeekId = row.weekId ?? null;
                            const isNotPresentWeek = presentWeekId == null || rowWeekId == null || Number(rowWeekId) !== Number(presentWeekId);
                            const disabled = isAssigned || isNotPresentWeek;
                            const tooltipTitle = isNotPresentWeek ? 'Assignment disabled for non-current weeks' : (isAssigned ? 'Already assigned' : 'Assign activity');
                            return (
                              <Tooltip title={tooltipTitle}>
                                <span>
                                  <IconButton
                                    size="small"
                                    sx={{ color: '#124b84' }}
                                    disabled={disabled}
                                    onClick={() => {
                                      if (disabled) return;
                                      setSelectedEmployeeId(Number(row.id));
                                      setOpenAssignModal(true);
                                    }}
                                  >
                                    <PersonAddAlt1Icon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            );
                          })()}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              )}

          <AssignActivityModal
            open={openAssignModal}
            onClose={() => setOpenAssignModal(false)}
            employeeId={selectedEmployeeId}
            categories={categories}
            technologies={technologies}
            employees={employees}
            activities={activitiesList}
            // selectedWeek={selectedWeek ? String(selectedWeek) : ''}
            onAssign={async (payload) => {
              try {
                const benchId = Number(selectedEmployeeId || payload.employeeId);
                // Prevent assigning the same employee to the same activity in the same week
                try {
                  const benchRecord = (activities || []).find(a => Number(a.benchId) === Number(benchId));
                  const empCode = benchRecord?.empCode ?? benchRecord?.empId ?? null;
                  const activityIdToCheck = payload.activityId ?? null;
                  const activityNameToCheck = payload.name ? String(payload.name).trim().toLowerCase() : null;
                  const weekForBench = benchRecord ? (benchRecord.weekID ?? benchRecord.weekId ?? benchRecord.week ?? null) : selectedWeek;

                  if (empCode != null && weekForBench != null) {
                    const exists = (activities || []).some(a => {
                      const aEmp = a.empCode ?? a.empId;
                      const aWeek = a.weekID ?? a.weekId ?? a.week ?? null;
                      const sameWeek = Number(aWeek) === Number(weekForBench);
                      if (!sameWeek) return false;
                      if (Number(aEmp) !== Number(empCode)) return false;
                      if (activityIdToCheck && (a.activityId || a.activityID || a.ActivityId)) {
                        return Number(a.activityId ?? a.activityID ?? a.ActivityId) === Number(activityIdToCheck);
                      }
                      if (activityNameToCheck) {
                        return String(a.activityName || '').trim().toLowerCase() === activityNameToCheck;
                      }
                      return false;
                    });

                    if (exists) {
                      // Return a structured duplicate response so the modal can show a friendly message
                      return { duplicate: true, message: 'This employee is already assigned to the same activity in the selected week.' };
                    }
                  }
                } catch (dupErr) {
                  // if duplicate-check fails for any reason, continue and let server-side handle validation
                  console.warn('Duplicate-check failed', dupErr);
                }
                const completedInPast = await findCompletedInPastWeek(benchId, payload);
                if (completedInPast) {
                  setReassignConfirm({
                    open: true,
                    week: completedInPast.week,
                    activityName: completedInPast.activityName,
                    employeeName: completedInPast.employeeName,
                    payload,
                    benchId,
                  });
                  return { cancelled: true };
                }

                setOpenAssignModal(false);
                await executeAssignment(payload, benchId);
                return Promise.resolve();
              } catch (err) {
                console.error("Optimistic assignment failed:", err);
                return Promise.reject(err);
              }
            }}
          />

          <Dialog
            open={reassignConfirm.open}
            onClose={() => setReassignConfirm(prev => ({ ...prev, open: false }))}
            maxWidth="sm"
            fullWidth
            PaperProps={{ className: 'reassign-confirm-dialog' }}
          >
            <DialogTitle className="reassign-confirm-header">
              <div className="reassign-confirm-title-wrap">
                <span className="reassign-confirm-icon-wrap">
                  <CheckCircleOutlineIcon className="reassign-confirm-icon" />
                </span>
                <span className="reassign-confirm-title">Activity Already Completed</span>
              </div>
              <IconButton
                size="small"
                onClick={() => setReassignConfirm(prev => ({ ...prev, open: false }))}
                className="reassign-confirm-close"
                aria-label="Close"
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              <div className="reassign-confirm-body">
                {reassignConfirm.employeeName || 'This employee'} has completed {reassignConfirm.activityName || 'this activity'} on{' '}
                {reassignConfirm.week
                  ? new Date(reassignConfirm.week).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                  : ''}{' '}
                week.
                <br />
                Do you still want to assign this activity?
              </div>
            </DialogContent>
            <DialogActions className="reassign-confirm-actions">
              <Button
                onClick={async () => {
                  const pending = reassignConfirm;
                  setReassignConfirm(prev => ({ ...prev, open: false }));
                  if (!pending.payload || !pending.benchId) {
                    return;
                  }
                  setOpenAssignModal(false);
                  try {
                    await executeAssignment(pending.payload, Number(pending.benchId));
                  } catch (err) {
                    setBanner({ type: 'error', message: 'Assignment failed: ' + (err && (err as any).message ? (err as any).message : String(err)) });
                  }
                }}
                variant="contained"
                className="reassign-confirm-yes"
              >
                Yes, Assign Anyway
              </Button>
              <Button
                onClick={() => {
                  setReassignConfirm(prev => ({ ...prev, open: false }));
                  setOpenAssignModal(true);
                }}
                variant="outlined"
                className="reassign-confirm-no"
              >
                No, Cancel
              </Button>
            </DialogActions>
          </Dialog>

        {/* Toast Banner */}
        <Snackbar
          open={!!banner}
          autoHideDuration={4000}
          onClose={() => setBanner(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          sx={{ zIndex: 1400, mt: 0.5 }}
        >
          <Alert
            onClose={() => setBanner(null)}
            severity={banner?.type || 'success'}
            icon={banner?.type === 'success' ? <CheckCircleIcon fontSize="inherit" sx={{ color: '#2e7d32' }} /> : undefined}
            sx={{
              width: '100%',
              fontWeight: 500,
              fontSize: 15,
              alignItems: 'center',
              borderRadius: '8px',
              border: banner?.type === 'success' ? '1px solid #b7dfc2' : '1px solid #f1b0ab',
              backgroundColor: banner?.type === 'success' ? '#e6f4ea' : '#fdecea',
              color: banner?.type === 'success' ? '#2e7d32' : '#d32f2f',
              boxShadow: 3,
              '& .MuiAlert-icon': {
                color: banner?.type === 'success' ? '#2e7d32' : '#d32f2f',
              },
              '& .MuiAlert-action .MuiIconButton-root': {
                color: banner?.type === 'success' ? '#2e7d32' : '#d32f2f',
                padding: '2px',
              },
              '@media (prefers-color-scheme: dark)': {
                backgroundColor: banner?.type === 'success' ? '#1b2e1b' : '#2d1616',
                color: banner?.type === 'success' ? '#7ee787' : '#ffb4ab',
                border: banner?.type === 'success' ? '1px solid #2f6f41' : '1px solid #7f1d1d',
              },
            }}
          >
            <span className="app-snackbar-message">{banner?.message || ''}</span>
          </Alert>
        </Snackbar>
        </div>

        {/* PAGINATION */}
        {/* Show pagination only if there are records */}
        {sortedData.length > 0 && (
          <div className="assign-pagination">
            <div className="pagination-info">
              {sortedData.length === 0
                ? 0
                : page * ITEMS_PER_PAGE + 1}{" "}
              to{" "}
              {Math.min((page + 1) * ITEMS_PER_PAGE, sortedData.length)}{" "}
              of {sortedData.length}  | Page {page + 1} of {totalPages}
            </div>
            <button
              className="page-btn first-btn"
              disabled={page === 0}
              onClick={() => setPage(0)}
            >
              First
            </button>
            <button
              className="page-btn prev-btn"
              disabled={isPrevDisabled}
              onClick={() => {
                const newPage = Math.max(0, page - 1);
                setPage(newPage);
              }}
            >
              Previous
            </button>
            <div className="page-numbers">
              {(() => {
                const blockSize = 5;
                const currentBlock = Math.floor(page / blockSize);
                const startPage = currentBlock * blockSize + 1;
                const endPage = Math.min(startPage + blockSize - 1, totalPages);
                const pageButtons = [];
                for (let p = startPage; p <= endPage; p++) {
                  pageButtons.push(
                    <button
                      key={p}
                      className={`page-number ${p === page + 1 ? "active" : ""}`}
                      onClick={() => setPage(p - 1)}
                    >
                      {p}
                    </button>
                  );
                }
                return pageButtons;
              })()}
            </div>
            <button
              className="page-btn next-btn"
              disabled={isNextDisabled}
              onClick={() => {
                const newPage = Math.min(totalPages - 1, page + 1);
                setPage(newPage);
              }}
            >
              Next
            </button>
            <button
              className="page-btn last-btn"
              disabled={page === totalPages - 1}
              onClick={() => setPage(totalPages - 1)}
            >
              Last
            </button>
          </div>
        )}

      <AddEmployeeModal
        open={openAddEmployee}
        onClose={() => setOpenAddEmployee(false)}
        employees={employees}
        onCreate={handleCreateEmployees}
      />
   </div>
  );
};

export default AssignActivity;
